import type { ReactNode } from 'react';
import { Eyebrow, Glow, Reveal } from '@byaft/ui';
import { usePageMeta } from '@byaft/ui/seo';

const LAST_UPDATED = '30 June 2026';

const linkClass = 'text-primary-container underline-offset-2 hover:underline';

function Section({ title, children }: { title: string; children: ReactNode }) {
  // Each section reveals on its own — the whole policy is far taller than the
  // viewport, so a single Reveal would never meet the IntersectionObserver
  // threshold and would stay invisible.
  return (
    <Reveal className="mt-10">
      <section>
        <h2 className="font-display text-xl font-bold text-on-background">{title}</h2>
        <div className="mt-3 space-y-3 text-body-md leading-relaxed text-on-surface-variant">{children}</div>
      </section>
    </Reveal>
  );
}

export function Privacy() {
  usePageMeta({
    title: 'Privacy Policy',
    seoTitle: 'Privacy Policy | BYAFT',
    seoDescription:
      'How BYAFT handles personal data across byaft.com and its services, in line with the UAE Personal Data Protection Law (Federal Decree-Law No. 45 of 2021).',
  });

  return (
    <article className="relative overflow-hidden pb-stack-xl pt-36 sm:pt-44">
      <Glow className="left-1/2 top-0 h-[50vw] max-h-[440px] w-[80vw] max-w-[760px] -translate-x-1/2 blur-[120px] opacity-60" />

      <div className="container-byaft">
        <Reveal className="max-w-3xl">
          <Eyebrow icon="eye" className="mb-6">
            Privacy
          </Eyebrow>
          <h1 className="text-headline-xl text-on-background sm:text-display-sm">Privacy Policy</h1>
          <p className="mt-4 font-display text-sm font-bold uppercase tracking-widest text-on-surface-variant">
            Last updated: {LAST_UPDATED}
          </p>
        </Reveal>

        <div className="mt-8 max-w-3xl">
          <Section title="1. Introduction">
            <p>
              BYAFT (&ldquo;BYAFT&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;) operates
              byaft.com and its related services, including the free browser-based tools at
              tools.byaft.com and BYAFT Cyber at cyber.byaft.com (together, the &ldquo;Service&rdquo;).
              This Privacy Policy explains how we handle personal data when you use the Service.
            </p>
            <p>
              We are committed to protecting your privacy in line with the United Arab Emirates Federal
              Decree-Law No. 45 of 2021 on the Protection of Personal Data (the &ldquo;PDPL&rdquo;) and its
              implementing regulations, as overseen by the UAE Data Office. By using the Service, you
              acknowledge the practices described in this policy.
            </p>
          </Section>

          <Section title="2. Our privacy-first approach">
            <p>
              BYAFT is built to collect as little personal data as possible. Our online tools run entirely
              inside your web browser: the documents, files, text and other content you create or open in a
              tool are processed on your own device and are never transmitted to, stored on, or seen by our
              servers.
            </p>
          </Section>

          <Section title="3. Personal data we collect">
            <p>We collect personal data in a few limited ways:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-on-background">Information you choose to provide.</strong> The
                Service does not require an account, sign-up or login to use the tools. If you contact us
                directly (for example, by email), we receive the information you include in that message.
              </li>
              <li>
                <strong className="text-on-background">Email sign-ups.</strong> If you subscribe to our
                updates or join a waitlist (such as the BYAFT Cyber waitlist), we collect the email address
                you submit and the date you submitted it, so we can send you the emails you asked for.
                Subscribing is entirely optional, and you can unsubscribe at any time.
              </li>
              <li>
                <strong className="text-on-background">Information collected automatically.</strong> When you
                visit the Service, we may use analytics technology that collects limited technical
                information about your visit — such as your device and browser type, approximate (city-level)
                location, the pages you view, the referring website, and your IP address (processed by our
                analytics provider and not stored by us in an identifiable form).
              </li>
            </ul>
            <p>
              We do not knowingly collect sensitive personal data (such as data revealing health, religious
              or political beliefs). Please do not enter such data where it is not needed.
            </p>
          </Section>

          <Section title="4. How and why we use personal data">
            <p>We use the limited personal data we collect to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>operate, maintain and secure the Service;</li>
              <li>understand how the Service is used so we can improve it;</li>
              <li>send you the email updates or waitlist messages you asked for;</li>
              <li>respond to your enquiries; and</li>
              <li>comply with our legal obligations.</li>
            </ul>
            <p>
              <strong className="text-on-background">Legal basis.</strong> Under the PDPL, we rely on your
              consent for non-essential analytics and for sending you email updates, and on the necessity of
              operating and securing the Service for the basic technical processing required to deliver it to
              you. You may withdraw your consent at any time — for the emails, use the unsubscribe link in
              any message we send you, or contact us (see &ldquo;Your rights&rdquo;).
            </p>
          </Section>

          <Section title="5. Cookies and analytics">
            <p>
              Where we use analytics (such as Google Analytics) to understand aggregate usage, it runs in a
              consent-aware mode that does not store analytics cookies until you agree, and we ask for your
              choice through a cookie banner where applicable. We use any information collected only in
              aggregate to measure traffic and improve the Service.
            </p>
            <p>
              You can also control or block cookies through your browser settings, and you can opt out of
              Google Analytics across all websites by installing the{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noreferrer noopener"
                className={linkClass}
              >
                Google Analytics Opt-out Browser Add-on
              </a>
              . For more on how Google handles data, see{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noreferrer noopener"
                className={linkClass}
              >
                Google&rsquo;s Privacy Policy
              </a>
              .
            </p>
          </Section>

          <Section title="6. Data stored in your browser">
            <p>
              Some tools save your drafts in your browser&rsquo;s local storage so your work is still there
              when you return. This data stays on your device, is not transmitted to us, and is not
              accessible to us. You can delete it at any time by clearing your browser&rsquo;s site data, or
              by using the relevant tool&rsquo;s delete or clear option.
            </p>
          </Section>

          <Section title="7. How we share personal data">
            <p>
              We do not sell your personal data. We share limited personal data only with the service
              providers that help us run the Service, namely:
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong className="text-on-background">Google</strong> (Google Analytics) — analytics
                processing, as described above; and
              </li>
              <li>
                <strong className="text-on-background">Cloudflare</strong> — hosting and delivery of the
                websites, and, where you submit your email, secure storage of your address and delivery of
                our emails.
              </li>
            </ul>
            <p>
              These providers act as our processors and may use the data only to provide their services to
              us. We may also disclose personal data where required to comply with applicable law or a lawful
              request from a competent authority.
            </p>
          </Section>

          <Section title="8. International data transfers">
            <p>
              Because the Service relies on global providers such as Google and Cloudflare, limited personal
              data may be processed on servers located outside the United Arab Emirates. Where personal data
              is transferred outside the UAE, we take steps to ensure it is handled in line with the
              cross-border transfer requirements of the PDPL and that an adequate level of protection is
              maintained.
            </p>
          </Section>

          <Section title="9. Data retention">
            <p>
              Content you create in our tools is retained only in your own browser, for as long as you keep
              it. Analytics data is retained by our analytics provider for a limited period, after which it
              is deleted or anonymised. If you contact us, we keep your message only as long as needed to
              handle your request. If you give us your email address, we keep it until you unsubscribe or ask
              us to remove it, after which it is deleted.
            </p>
          </Section>

          <Section title="10. Data security">
            <p>
              The Service is served over encrypted HTTPS connections, and because tool content is processed
              locally on your device it is not exposed to our servers. While no method of transmission or
              storage is completely secure, we apply appropriate technical and organisational measures to
              protect the limited personal data we handle.
            </p>
          </Section>

          <Section title="11. Your rights under UAE law">
            <p>Subject to the conditions and exceptions in the PDPL, you have the right to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>be informed about how your personal data is processed, and to access it;</li>
              <li>request the correction of inaccurate or incomplete personal data;</li>
              <li>request the deletion of your personal data;</li>
              <li>request that we restrict or stop the processing of your personal data;</li>
              <li>request the transfer of your personal data (data portability);</li>
              <li>object to processing carried out by automated means; and</li>
              <li>
                withdraw your consent to processing at any time, without affecting processing carried out
                before the withdrawal.
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us using the details in &ldquo;Contact us&rdquo; below.
              We will respond in accordance with the PDPL.
            </p>
          </Section>

          <Section title="12. Children's privacy">
            <p>
              The Service is intended for general audiences and is not directed at children under the age of
              18. We do not knowingly collect personal data from children. If you believe a child has provided
              us with personal data, please contact us and we will take appropriate steps to delete it.
            </p>
          </Section>

          <Section title="13. Complaints">
            <p>
              If you have concerns about how your personal data is handled, we encourage you to contact us
              first so we can help. You also have the right to lodge a complaint with the UAE Data Office, the
              federal authority responsible for personal data protection in the United Arab Emirates.
            </p>
          </Section>

          <Section title="14. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or the
              law. When we do, we will revise the &ldquo;Last updated&rdquo; date at the top of this page. We
              encourage you to review this page periodically.
            </p>
          </Section>

          <Section title="15. Contact us">
            <p>
              If you have any questions about this Privacy Policy or wish to exercise your rights, you can
              reach us at:
            </p>
            <p>
              Email:{' '}
              <a href="mailto:privacy@byaft.com" className={linkClass}>
                privacy@byaft.com
              </a>
            </p>
            <p>You can also find BYAFT on TikTok, Instagram and X.</p>
          </Section>
        </div>
      </div>
    </article>
  );
}
